
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { toast } from 'sonner';
import type { Ticket, TicketReply, CreateTicketData, CreateReplyData } from '@/types/ticket';

export function useTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useAuthSession();

  const fetchTickets = async () => {
    if (!session?.user) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('user_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const createTicket = async (ticketData: CreateTicketData): Promise<boolean> => {
    if (!session?.user) {
      toast.error('You must be logged in to create a ticket');
      return false;
    }

    try {
      const { error } = await supabase
        .from('user_tickets')
        .insert({
          ...ticketData,
          user_id: session.user.id
        });

      if (error) throw error;

      toast.success('Ticket created successfully');
      fetchTickets();
      return true;
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
      return false;
    }
  };

  const updateTicketStatus = async (ticketId: string, status: Ticket['status']): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('user_tickets')
        .update({ 
          status,
          resolved_at: status === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', ticketId);

      if (error) throw error;

      toast.success('Ticket status updated');
      fetchTickets();
      return true;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Failed to update ticket status');
      return false;
    }
  };

  useEffect(() => {
    if (session?.user) {
      fetchTickets();
    }
  }, [session?.user]);

  return {
    tickets,
    isLoading,
    createTicket,
    updateTicketStatus,
    refreshTickets: fetchTickets
  };
}

export function useTicketReplies(ticketId: string) {
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useAuthSession();

  const fetchReplies = async () => {
    if (!ticketId) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setReplies(data || []);
    } catch (error) {
      console.error('Error fetching replies:', error);
      toast.error('Failed to load replies');
    } finally {
      setIsLoading(false);
    }
  };

  const createReply = async (replyData: CreateReplyData): Promise<boolean> => {
    if (!session?.user) {
      toast.error('You must be logged in to reply');
      return false;
    }

    try {
      const { error } = await supabase
        .from('ticket_replies')
        .insert({
          ...replyData,
          user_id: session.user.id
        });

      if (error) throw error;

      toast.success('Reply sent successfully');
      fetchReplies();
      return true;
    } catch (error) {
      console.error('Error creating reply:', error);
      toast.error('Failed to send reply');
      return false;
    }
  };

  useEffect(() => {
    if (ticketId) {
      fetchReplies();
    }
  }, [ticketId]);

  return {
    replies,
    isLoading,
    createReply,
    refreshReplies: fetchReplies
  };
}
